import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Check, Upload, Loader2, ShieldCheck, Wallet, PenLine, Eye } from "lucide-react";
import { toast } from "sonner";
import { Panel, Field } from "../components/ui";
import { HeroPanel, Step, Metric } from "../components/ui-extras";
import TierBadge from "../components/TierBadge";
import Disclaimer from "../components/Disclaimer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useSeo from "../lib/useSeo";
import usePricing from "../lib/pricing";
import { money, price as fmtPrice } from "../lib/format";
import { useTranslation } from "react-i18next";

const TIERS = ["bronze", "silver", "gold", "platinum"];

/**
 * Recruitment page for investors, with the application form inline.
 *
 * Public so it can be linked and indexed. The form only appears for a signed-in
 * member — asking someone to write two paragraphs before they have an account is
 * how you lose the application.
 */
export default function ForInvestors() {
  const { t } = useTranslation();
  const { user, role, features } = useAuth();
  // Every figure quoted on this page is a promise about money, so all of them come
  // from the same server settings the payouts are actually computed from — and while
  // payments are off (backend/features.py) there is no money to promise: the
  // earnings panel and the "earn" reason are not shown.
  const pricing = usePricing({ enabled: features.payments });
  const shares = pricing?.share_pct;
  const perTier = pricing?.plans?.pro?.per_tier_eur;
  const ccy = pricing?.currency || "EUR";
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ experience: "", strategy: "", track_record_years: "", links: "" });
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  useSeo({ title: t("forInvestors.seoTitle"), description: t("forInvestors.seoDesc"), path: "/for-investors" });

  useEffect(() => {
    if (user) api.myApplication().then((r) => setStatus(r.data.status)).catch(() => {});
  }, [user]);

  const pickDoc = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error(t("forInvestors.tooBig")); return; }
    const reader = new FileReader();
    reader.onload = () => setDoc({ data: reader.result, name: f.name });
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.apply({
        experience: form.experience, strategy: form.strategy,
        track_record_years: Number(form.track_record_years) || 0,
        links: form.links.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
        document: doc?.data, filename: doc?.name,
      });
      setStatus("pending");
      toast.success(t("forInvestors.submitted"));
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    finally { setBusy(false); }
  };

  const alreadyInvestor = role === "investor" || role === "admin";

  return (
    <div className="max-w-3xl mx-auto px-5 py-10 space-y-8">
      <HeroPanel eyebrow={t("forInvestors.eyebrow")} title={t("forInvestors.title")}
                 subtitle={t("forInvestors.subtitle")} testid="fi-hero">
        <div className="grid grid-cols-3 gap-5">
          <Metric tone="inverse" label={t("forInvestors.m1")}
                  value={shares ? `${shares.platinum}%` : "—"} sub={t("forInvestors.m1sub")} />
          <Metric tone="inverse" label={t("forInvestors.m2")} value={fmtPrice(0, ccy)}
                  sub={t("forInvestors.m2sub")} />
          <Metric tone="inverse" label={t("forInvestors.m3")} value="100%" sub={t("forInvestors.m3sub")} />
        </div>
      </HeroPanel>

      <Panel className="card p-6" testid="fi-why">
        <h2 className="font-heading font-semibold tracking-tight text-xl">{t("forInvestors.whyTitle")}</h2>
        <div className="mt-4 space-y-5">
          {[["control", Eye], ["earn", Wallet], ["audience", TrendingUp], ["writing", PenLine]]
            .filter(([k]) => k !== "earn" || features.payments).map(([k, Icon]) => (
            <div key={k} className="flex gap-4">
              <div className="w-9 h-9 rounded-xl bg-brand-50 text-brand-600 grid place-items-center shrink-0">
                <Icon size={17} strokeWidth={1.8} />
              </div>
              <div>
                <div className="font-medium text-slate-900">{t(`forInvestors.why.${k}.t`)}</div>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                  {t(`forInvestors.why.${k}.b`,
                     { min: shares?.bronze ?? "—", max: shares?.platinum ?? "—" })}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {features.payments && (
      <Panel className="card p-6" testid="fi-earnings">
        <h2 className="font-heading font-semibold tracking-tight text-xl">{t("forInvestors.earnTitle")}</h2>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          {t("forInvestors.earnBody", { price: fmtPrice(pricing?.plans?.pro?.gross_eur, ccy) })}
        </p>
        <div className="mt-4 space-y-1">
          {TIERS.map((tier) => (
            <div key={tier} className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl hover:bg-slate-50">
              <TierBadge tier={tier} size="xs" />
              <span className="text-sm text-slate-500 tabular">
                {shares ? `${shares[tier]}%` : "—"}
              </span>
              {/* money(), not the headline formatter: a column of payouts wants
                  "€2.00" lined up under "€2.59", not "€2". */}
              <span className="text-sm font-semibold tabular">
                {perTier ? money(perTier[tier], ccy) : "—"}
              </span>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-3">{t("forInvestors.earnNote")}</p>
      </Panel>
      )}

      <Panel className="card p-6" testid="fi-how">
        <h2 className="font-heading font-semibold tracking-tight text-xl mb-4">{t("forInvestors.howTitle")}</h2>
        <Step n={1} title={t("forInvestors.s1")}>{t("forInvestors.s1b")}</Step>
        <Step n={2} title={t("forInvestors.s2")}>{t("forInvestors.s2b")}</Step>
        <Step n={3} title={t("forInvestors.s3")}>{t("forInvestors.s3b")}</Step>
        <Step n={4} title={t("forInvestors.s4")}>{t("forInvestors.s4b")}</Step>
      </Panel>

      {/* Application */}
      <Panel className="card p-6" testid="fi-apply">
        <h2 className="font-heading font-semibold tracking-tight text-xl">{t("forInvestors.applyTitle")}</h2>

        {alreadyInvestor ? (
          <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-sm text-emerald-800">
            {t("forInvestors.alreadyInvestor")}{" "}
            <Link to="/studio" className="underline font-medium">{t("nav.studio")}</Link>
          </div>
        ) : !user ? (
          <div className="mt-4">
            <p className="text-sm text-slate-600">{t("forInvestors.signInFirst")}</p>
            <Link to="/register" className="btn-primary mt-4 inline-flex" data-testid="fi-register">
              {t("home.getStarted")} <ArrowRight size={16} />
            </Link>
          </div>
        ) : status === "pending" ? (
          <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm text-amber-900"
               data-testid="fi-pending">
            {t("forInvestors.pending")}
          </div>
        ) : status === "rejected" ? (
          <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm text-slate-600">{t("forInvestors.rejected")}</div>
        ) : (
          <div className="mt-4 space-y-4">
            <Field label={t("forInvestors.fExperience")}>
              <textarea className="input min-h-[110px] resize-none" data-testid="apply-experience"
                        placeholder={t("forInvestors.fExperiencePh")}
                        value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} />
            </Field>
            <Field label={t("forInvestors.fStrategy")}>
              <textarea className="input min-h-[110px] resize-none" data-testid="apply-strategy"
                        placeholder={t("forInvestors.fStrategyPh")}
                        value={form.strategy} onChange={(e) => setForm({ ...form, strategy: e.target.value })} />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("forInvestors.fYears")}>
                <input type="number" className="input tabular" data-testid="apply-years"
                       value={form.track_record_years}
                       onChange={(e) => setForm({ ...form, track_record_years: e.target.value })} />
              </Field>
              <Field label={t("forInvestors.fLinks")}>
                <input className="input" data-testid="apply-links" placeholder="https://…"
                       value={form.links} onChange={(e) => setForm({ ...form, links: e.target.value })} />
              </Field>
            </div>
            <Field label={t("forInvestors.fDoc")}>
              <button type="button" className="btn-outline w-full justify-center" data-testid="apply-doc"
                      onClick={() => fileRef.current?.click()}>
                <Upload size={15} /> {doc ? doc.name : t("forInvestors.fDocBtn")}
              </button>
              <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg"
                     className="hidden" onChange={pickDoc} />
              <p className="text-[11px] text-slate-400 mt-2 flex items-start gap-1.5">
                <ShieldCheck size={12} className="mt-0.5 shrink-0" /> {t("forInvestors.fDocNote")}
              </p>
            </Field>
            <button className="btn-primary w-full justify-center" onClick={submit} disabled={busy}
                    data-testid="apply-submit">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              {t("forInvestors.submit")}
            </button>
          </div>
        )}
      </Panel>

      <Disclaimer />
    </div>
  );
}
